import { useState } from "react";
import {
  deriveAuthSecret,
  deriveRecoveryAuthProof,
  deriveRecoveryWrapKey,
  derivePasswordWrapKey,
  isValidMnemonic,
  unwrapChatKey,
  wrapChatKey
} from "../lib/crypto.js";
import { confirmRecovery, getRecoveryStart } from "../lib/api.js";

interface RecoveryProps {
  onSwitchToLogin: () => void;
}

type Stage = "mnemonic" | "new-password" | "done";

/**
 * Forgot-password recovery (Decision 4, Revision 5). Two stages:
 *   1. Username + 12-word recovery mnemonic -> `recovery-start` -> unwrap
 *      `chatKey` locally using `recoveryWrapKey`.
 *   2. New password -> re-wrap the (already-unwrapped) `chatKey` under a
 *      fresh `passwordWrapKey`, derive `recoveryAuthProof` (server-verified
 *      before it accepts the new password), call `recovery-confirm`.
 *
 * Deliberately does not auto-login afterward — `recovery-confirm` never
 * issues a device token (see its own comment), so the user logs in normally
 * with the new password from here, keeping this endpoint's blast radius
 * limited to "reset the password/rewrap," nothing else.
 */
export default function Recovery({ onSwitchToLogin }: RecoveryProps) {
  const [stage, setStage] = useState<Stage>("mnemonic");
  const [username, setUsername] = useState("");
  const [mnemonic, setMnemonic] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "working" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [salt, setSalt] = useState<string | null>(null);
  const [chatKey, setChatKey] = useState<string | null>(null);

  async function handleMnemonicSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrorMessage(null);

    const trimmedUsername = username.trim();
    if (!trimmedUsername || !mnemonic.trim()) {
      setErrorMessage("Enter your username and 12-word recovery phrase.");
      return;
    }
    if (!isValidMnemonic(mnemonic)) {
      setErrorMessage("That doesn't look like a valid 12-word recovery phrase.");
      return;
    }

    setStatus("working");
    try {
      const { salt: accountSalt, wrappedChatKeyRecovery } = await getRecoveryStart(trimmedUsername);
      const recoveryWrapKey = await deriveRecoveryWrapKey(mnemonic, accountSalt);
      const unwrapped = unwrapChatKey(wrappedChatKeyRecovery, recoveryWrapKey);
      if (!unwrapped) {
        setErrorMessage("Incorrect username or recovery phrase.");
        setStatus("error");
        return;
      }
      setSalt(accountSalt);
      setChatKey(unwrapped);
      setStage("new-password");
      setStatus("idle");
    } catch {
      // Deliberately generic — mirrors the server's own non-disclosure of
      // "which part was wrong" (Done criteria).
      setErrorMessage("Incorrect username or recovery phrase.");
      setStatus("error");
    }
  }

  async function handleNewPasswordSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrorMessage(null);

    if (!salt || !chatKey) return;
    if (newPassword.length < 8) {
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setStatus("working");
    try {
      const [newAuthSecret, newPasswordWrapKey, recoveryAuthProof] = await Promise.all([
        deriveAuthSecret(newPassword, salt),
        derivePasswordWrapKey(newPassword, salt),
        deriveRecoveryAuthProof(mnemonic, salt)
      ]);
      const newWrappedChatKeyPassword = wrapChatKey(chatKey, newPasswordWrapKey);

      await confirmRecovery({
        username: username.trim(),
        recoveryAuthProof,
        newAuthSecret,
        newWrappedChatKeyPassword
      });

      setStage("done");
      setStatus("idle");
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "recovery_failed");
      setStatus("error");
    }
  }

  if (stage === "done") {
    return (
      <div className="rounded-xl border border-slate-800 bg-gray-900 p-4">
        <h1 className="text-xl font-semibold text-slate-100">Password reset</h1>
        <p className="mt-2 text-sm text-slate-300">
          Your password has been reset. Log in with your new password to continue.
        </p>
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="mt-4 cursor-pointer rounded-lg bg-blue-600 px-4 py-2.5 text-white"
        >
          Back to log in
        </button>
      </div>
    );
  }

  if (stage === "new-password") {
    return (
      <div className="rounded-xl border border-slate-800 bg-gray-900 p-4">
        <h1 className="text-xl font-semibold text-slate-100">Choose a new password</h1>
        <p className="mt-2 text-sm text-slate-300">Recovery phrase verified — your messages are recoverable.</p>
        <form onSubmit={handleNewPasswordSubmit} className="mt-3 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            New password
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-slate-200"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            Confirm new password
            <input
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-slate-200"
            />
          </label>
          <button
            type="submit"
            disabled={status === "working" || !newPassword}
            className="cursor-pointer rounded-lg bg-blue-600 px-4 py-2.5 text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "working" ? "Resetting…" : "Reset password"}
          </button>
          {status === "error" && errorMessage && <p className="text-sm text-red-400">{errorMessage}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-800 bg-gray-900 p-4">
      <h1 className="text-xl font-semibold text-slate-100">Recover your account</h1>
      <p className="mt-2 text-sm text-slate-300">
        Enter your username and the 12-word recovery phrase you saved when you created your account.
      </p>
      <form onSubmit={handleMnemonicSubmit} className="mt-3 flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm text-slate-300">
          Username
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-slate-200"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-slate-300">
          Recovery phrase (12 words)
          <textarea
            value={mnemonic}
            onChange={(e) => setMnemonic(e.target.value)}
            rows={3}
            placeholder="word1 word2 word3 ..."
            className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-slate-200"
          />
        </label>
        <button
          type="submit"
          disabled={status === "working" || !username.trim() || !mnemonic.trim()}
          className="cursor-pointer rounded-lg bg-blue-600 px-4 py-2.5 text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "working" ? "Verifying…" : "Verify recovery phrase"}
        </button>
        {status === "error" && errorMessage && <p className="text-sm text-red-400">{errorMessage}</p>}
      </form>
      <p className="mt-3 text-sm text-slate-400">
        <button type="button" onClick={onSwitchToLogin} className="cursor-pointer border-none bg-transparent p-0 text-blue-400">
          Back to log in
        </button>
      </p>
    </div>
  );
}
